import { titleCase } from "../geo/format.ts";
import { hasTagValue, tagMatcher, tagValue } from "./tags.ts";
import type { OsmTags } from "./types.ts";

/**
 * App-level place classification derived from raw OSM tags.
 */
export interface OsmPlaceKind {
  /**
   * Readable place type for UI copy, such as "Cafe" or "Bicycle shop".
   * This is derived from a curated rule or from the primary OSM identity tag.
   */
  typeLabel: string;
}

type PlaceKindRule = {
  label: string | ((tags: OsmTags) => string);
  matches: (tags: OsmTags) => boolean;
};

/**
 * Classifies an OSM element's raw `tags` into a small app-owned place kind.
 *
 * This deliberately avoids promising OpenMapTiles class/subclass parity or full
 * OSM taxonomy coverage.
 */
export function osmPlaceKind(tags: OsmTags): OsmPlaceKind | undefined {
  // First, try to match a curated rule for a specific combination of tags.
  const rule = PLACE_KIND_RULES.find((candidate) => candidate.matches(tags));
  if (rule) {
    const typeLabel = typeof rule.label === "function"
      ? rule.label(tags)
      : rule.label;
    return { typeLabel };
  }

  // If no curated rule matches, fall back to a generic label based on the most relevant OSM tag.
  const primary = primaryOsmTag(tags);
  if (primary) {
    return { typeLabel: fallbackLabel(primary.key, primary.value) };
  }

  return undefined;
}

// Policy for this mapping:
// - Rules are ordered from specific to broad. First match wins.
// - Identity tags such as amenity/shop/tourism/leisure define the place type.
//   Secondary tags such as cuisine/religion/sport may refine an identity match,
//   but should not create a place type on their own unless we explicitly decide
//   the standalone tag is useful.
// - Prefer a generic correct label over a specific questionable label. Add a
//   special case only when it improves visible copy or enables concrete app UI.
// - Keep the set finite and reviewable. Unknown combinations fall back to the
//   primary OSM tag label, while the raw tags remain visible in the debug table.
const PLACE_KIND_RULES: PlaceKindRule[] = [
  {
    label: "Christian place of worship",
    matches: (tags) =>
      tagValue(tags, "amenity") === "place_of_worship" &&
      tagValue(tags, "religion") === "christian",
  },
  {
    label: "Jewish place of worship",
    matches: (tags) =>
      tagValue(tags, "amenity") === "place_of_worship" &&
      tagValue(tags, "religion") === "jewish",
  },
  {
    label: "Place of worship",
    matches: tagMatcher("amenity", "place_of_worship"),
  },
  {
    label: "Guidepost",
    matches: (tags) =>
      tagValue(tags, "tourism") === "information" &&
      tagValue(tags, "information") === "guidepost",
  },
  {
    label: "Tourism information",
    matches: tagMatcher("tourism", "information"),
  },
  {
    label: "Subway station",
    matches: (tags) =>
      tagValue(tags, "railway") === "station" &&
      tagValue(tags, "station") === "subway",
  },
  {
    label: "Funicular station",
    matches: (tags) =>
      tagValue(tags, "railway") === "station" &&
      tagValue(tags, "funicular") === "yes",
  },
  {
    label: "Station",
    matches: tagMatcher("railway", "station"),
  },
  {
    label: "Aerialway station",
    matches: tagMatcher("aerialway", "station"),
  },
  {
    label: "Private garden",
    matches: (tags) =>
      tagValue(tags, "leisure") === "garden" &&
      tagValue(tags, "access") === "private",
  },
  {
    label: "Soccer pitch",
    matches: (tags) =>
      tagValue(tags, "leisure") === "pitch" &&
      tagValue(tags, "sport") === "soccer",
  },
  {
    label: (tags) => `${fallbackLabel("sport", tagValue(tags, "sport"))} pitch`,
    matches: (tags) =>
      tagValue(tags, "leisure") === "pitch" && hasTagValue(tags.sport),
  },
  {
    label: "Pizza restaurant",
    matches: (tags) =>
      tagValue(tags, "amenity") === "restaurant" &&
      tagValue(tags, "cuisine") === "pizza",
  },
  {
    label: "Restaurant",
    matches: tagMatcher("amenity", "restaurant"),
  },
  {
    label: "Cafe",
    matches: tagMatcher("amenity", "cafe"),
  },
  {
    label: "Fast food",
    matches: tagMatcher("amenity", "fast_food"),
  },
  {
    label: "Ice cream shop",
    matches: tagMatcher("amenity", "ice_cream"),
  },
  {
    label: "DIY store",
    matches: tagMatcher("amenity", "do_it_yourself"),
  },
  {
    label: "Bus station",
    matches: tagMatcher("amenity", "bus_station"),
  },
  {
    label: "Bicycle parking",
    matches: tagMatcher("amenity", "bicycle_parking"),
  },
  {
    label: "Bicycle rental",
    matches: tagMatcher("amenity", "bicycle_rental"),
  },
  {
    label: "Car rental",
    matches: tagMatcher("amenity", "car_rental"),
  },
  {
    label: "Car sharing",
    matches: tagMatcher("amenity", "car_sharing"),
  },
  {
    label: "Ferry terminal",
    matches: tagMatcher("amenity", "ferry_terminal"),
  },
  {
    label: "Petrol station",
    matches: tagMatcher("amenity", "fuel"),
  },
  {
    label: "Parking area",
    matches: tagMatcher("amenity", "parking"),
  },
  {
    label: "Taxi stand",
    matches: tagMatcher("amenity", "taxi"),
  },
  {
    label: "Hospital",
    matches: tagMatcher("amenity", "hospital"),
  },
  {
    label: "Clinic",
    matches: tagMatcher("amenity", "clinic"),
  },
  {
    label: "Pharmacy",
    matches: tagMatcher("amenity", "pharmacy"),
  },
  {
    label: "Dentist",
    matches: tagMatcher("amenity", "dentist"),
  },
  {
    label: "Doctors",
    matches: tagMatcher("amenity", "doctors"),
  },
  {
    label: "Veterinary clinic",
    matches: tagMatcher("amenity", "veterinary"),
  },
  {
    label: "Social facility",
    matches: tagMatcher("amenity", "social_facility"),
  },
  {
    label: "Baby hatch",
    matches: tagMatcher("amenity", "baby_hatch"),
  },
  {
    label: "Bank",
    matches: tagMatcher("amenity", "bank"),
  },
  {
    label: "Bureau de change",
    matches: tagMatcher("amenity", "bureau_de_change"),
  },
  {
    label: "Money transfer",
    matches: tagMatcher("amenity", "money_transfer"),
  },
  {
    label: "Payment terminal",
    matches: tagMatcher("amenity", "payment_terminal"),
  },
  {
    label: "ATM",
    matches: tagMatcher("amenity", "atm"),
  },
  {
    label: "Parcel locker",
    matches: tagMatcher("amenity", "parcel_locker"),
  },
  {
    label: "Charging station",
    matches: tagMatcher("amenity", "charging_station"),
  },
  {
    label: "Courthouse",
    matches: tagMatcher("amenity", "courthouse"),
  },
  {
    label: "Fire station",
    matches: tagMatcher("amenity", "fire_station"),
  },
  {
    label: "Police",
    matches: tagMatcher("amenity", "police"),
  },
  {
    label: "Post box",
    matches: tagMatcher("amenity", "post_box"),
  },
  {
    label: "Post office",
    matches: tagMatcher("amenity", "post_office"),
  },
  {
    label: "Prison",
    matches: tagMatcher("amenity", "prison"),
  },
  {
    label: "Ranger station",
    matches: tagMatcher("amenity", "ranger_station"),
  },
  {
    label: "Town hall",
    matches: tagMatcher("amenity", "townhall"),
  },
  {
    label: "BBQ",
    matches: tagMatcher("amenity", "bbq"),
  },
  {
    label: "Bench",
    matches: tagMatcher("amenity", "bench"),
  },
  {
    label: "Drinking water",
    matches: tagMatcher("amenity", "drinking_water"),
  },
  {
    label: "Shelter",
    matches: tagMatcher("amenity", "shelter"),
  },
  {
    label: "Shower",
    matches: tagMatcher("amenity", "shower"),
  },
  {
    label: "Telephone",
    matches: tagMatcher("amenity", "telephone"),
  },
  {
    label: "Toilets",
    matches: tagMatcher("amenity", "toilets"),
  },
  {
    label: "Water point",
    matches: tagMatcher("amenity", "water_point"),
  },
  {
    label: "Waste basket",
    matches: tagMatcher("amenity", "waste_basket"),
  },
  {
    label: "Recycling center",
    matches: (tags) =>
      tagValue(tags, "amenity") === "recycling" &&
      tagValue(tags, "recycling_type") === "centre",
  },
  {
    label: "Recycling container",
    matches: (tags) =>
      tagValue(tags, "amenity") === "recycling" &&
      tagValue(tags, "recycling_type") === "container",
  },
  {
    label: "Recycling",
    matches: tagMatcher("amenity", "recycling"),
  },
  {
    label: "Region",
    matches: tagMatcher("place", "region"),
  },
  {
    label: "Aerodrome",
    matches: tagMatcher("aeroway", "aerodrome"),
  },
  {
    label: "Motorsport centre",
    matches: (tags) =>
      tagValue(tags, "leisure") === "sports_centre" &&
      tagValue(tags, "sport") === "motor",
  },
];

// Fallback path for OSM objects that do not match a curated rule above.
//
// OSM has many valid primary tags, and we do not want to enumerate every value.
// Instead, pick the first present identity tag from this priority list and turn
// its value into a readable label:
//
//   office=accountant -> "Accountant"
//   shop=locksmith -> "Locksmith"
//   building=yes -> "Building"
//   amenity=yes -> "Amenity"
//   amenity=waste_basket;recycling -> "Waste basket"
//
// The order is a product choice for conflicting tags, not an OSM law. Keep it
// conservative: tags that more often describe a selected POI's identity should
// come before broad geometry/context tags such as building/highway/waterway.
const PRIMARY_OSM_TAG_KEYS = [
  "amenity",
  "shop",
  "tourism",
  "leisure",
  "historic",
  "office",
  "craft",
  "natural",
  "building",
  "highway",
  "railway",
  "aerialway",
  "waterway",
] as const;

function primaryOsmTag(
  tags: OsmTags,
): { key: string; value: string } | undefined {
  for (const key of PRIMARY_OSM_TAG_KEYS) {
    const value = tagValue(tags, key);
    if (value) {
      return {
        key,
        value: value === "yes" ? key : value,
      };
    }
  }
  return undefined;
}

// Shop values where title-casing the raw tag is too terse or awkward for UI copy.
const SHOP_LABELS: Record<string, string> = {
  baby_goods: "Baby goods store",
  bag: "Bag store",
  bicycle: "Bicycle shop",
  boutique: "Clothing store",
  clothes: "Clothing store",
  fabric: "Fabric store",
  fashion: "Clothing store",
  fashion_accessories: "Fashion accessories store",
  jewelry: "Jewelry store",
  leather: "Leather goods store",
  sewing: "Sewing supply store",
  shoes: "Shoe store",
  shoe_repair: "Shoe repair",
  watches: "Watch store",
  wool: "Wool store",
};

const HIGHWAY_LABELS: Record<string, string> = {
  living_street: "Living street",
  motorway_link: "Motorway link road",
  pedestrian: "Pedestrian street",
  primary: "Primary road",
  primary_link: "Primary link road",
  residential: "Residential road",
  secondary: "Secondary road",
  secondary_link: "Secondary link road",
  service: "Service road",
  tertiary: "Tertiary road",
  tertiary_link: "Tertiary link road",
  trunk_link: "Trunk link road",
  unclassified: "Minor road",
};

// Special tag values that can't be title-cased into a display label.
const FALLBACK_LABELS: Record<string, string> = {
  atm: "ATM",
  bbq: "BBQ",
  cafe: "Cafe",
  charging_station: "Charging station",
  fast_food: "Fast food",
  place_of_worship: "Place of worship",
  post_box: "Post box",
  post_office: "Post office",
  parcel_locker: "Parcel locker",
  town_hall: "Town hall",
};

function fallbackLabel(key: string, value: string): string {
  if (key === "shop") {
    return SHOP_LABELS[value] ?? titleCase(value);
  }
  if (key === "highway") {
    return HIGHWAY_LABELS[value] ?? titleCase(value);
  }
  return FALLBACK_LABELS[value] ?? titleCase(value);
}
